import React, { useCallback, useState } from 'react';

import { Grid } from 'antd';

import { ForgotForm } from './forgot-form';
import { LoginForm } from './login-form';
import { StyledLoginModal } from './styles';
import { LottiePlayer } from '../../../components/atoms/lottie-player';
import { Modal } from '../../../components/atoms/modal';
import { GradientButton } from '../../../components/molecules/gradient-button';
import { useNotificationContext } from '../../../components/molecules/notification-context';
import { useAuth } from '../../../hooks/use-auth';
import {
  useFacebookSignInMutation,
  useFacebookSignUpMutation,
  useForgotPasswordMutation,
  useGoogleSignInMutation,
  useGoogleSignUpMutation,
  useSignInMutation,
} from '../../../store/queries/auth';
import { Heading11, Heading2, Heading4 } from '../../../theme';
import { login } from '../../../theme/assets/animations';
import { AnalyticsEvents, trackEvent, trackGoogleAdsConversion } from '../../../utils/analytics';
import { bemClassName } from '../../../utils/bem';
import { NOTIFICATION_TYPES } from '../../../utils/constants';
import { clearStoredReferralCode, getStoredReferralCode } from '../../../utils/referral';
import { lottieDefaultOptions } from '../../../utils/settings';
import { socialLoginRedirect } from '../../../utils/social-redirect';

const b = bemClassName('login-modal');

const getRedirectUrl = (data) => (data.onboarding.finished ? '/user/dashboard' : '/onboarding');

const LoginModal = ({ isOpen, onOk, onClose }) => {
  const notificationApi = useNotificationContext();
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const { userSignIn } = useAuth();

  const [activeForm, setActiveForm] = useState('login');
  const [isLoading, setIsLoading] = useState(false);

  const [pendingSocialSignUp, setPendingSocialSignUp] = useState(null);

  const [signIn] = useSignInMutation();
  const [googleSignIn] = useGoogleSignInMutation();
  const [googleSignUp] = useGoogleSignUpMutation();
  const [facebookSignIn] = useFacebookSignInMutation();
  const [facebookSignUp] = useFacebookSignUpMutation();
  const [forgotPassword] = useForgotPasswordMutation();

  const handleSignIn = useCallback(
    async (credentials) => {
      setIsLoading(true);

      try {
        const result = await signIn(credentials).unwrap();

        trackEvent(AnalyticsEvents.LOGIN, { method: 'email' });
        onClose();
        userSignIn({ token: result.access_token, redirectUrl: getRedirectUrl(result.data) });
      } catch (err) {
        const message =
          err?.status === 401
            ? 'Invalid email or password'
            : 'An error occurred while processing your request. Please try again later.';

        notificationApi[NOTIFICATION_TYPES.error]({ message, pauseOnHover: true });
      } finally {
        setIsLoading(false);
      }
    },
    [signIn, onClose, userSignIn, notificationApi],
  );

  const handleGoogleSignIn = useCallback(
    async (googleResponse) => {
      setIsLoading(true);

      try {
        const result = await googleSignIn({ token: googleResponse.credential }).unwrap();

        trackEvent(AnalyticsEvents.LOGIN, { method: 'google' });
        onClose();
        userSignIn({ token: result.access_token, redirectUrl: getRedirectUrl(result.data) });
      } catch (err) {
        if (err?.data?.code === 'ACCOUNT_NOT_FOUND') {
          setPendingSocialSignUp({ provider: 'google', token: googleResponse.credential });
        } else {
          notificationApi[NOTIFICATION_TYPES.error]({
            message: err?.data?.message || 'An error occurred during Google sign in. Please try again.',
            pauseOnHover: true,
          });
        }
      } finally {
        setIsLoading(false);
      }
    },
    [googleSignIn, onClose, userSignIn, notificationApi],
  );

  const handleFacebookSignIn = useCallback(
    async (facebookResponse) => {
      setIsLoading(true);

      try {
        const result = await facebookSignIn({ accessToken: facebookResponse.accessToken }).unwrap();

        trackEvent(AnalyticsEvents.LOGIN, { method: 'facebook' });
        onClose();
        userSignIn({ token: result.access_token, redirectUrl: getRedirectUrl(result.data) });
      } catch (err) {
        if (err?.data?.code === 'ACCOUNT_NOT_FOUND') {
          setPendingSocialSignUp({ provider: 'facebook', accessToken: facebookResponse.accessToken });
        } else {
          notificationApi[NOTIFICATION_TYPES.error]({
            message: err?.data?.message || 'An error occurred during Facebook sign in. Please try again.',
            pauseOnHover: true,
          });
        }
      } finally {
        setIsLoading(false);
      }
    },
    [facebookSignIn, onClose, userSignIn, notificationApi],
  );

  const handleConfirmSocialSignUp = useCallback(async () => {
    if (!pendingSocialSignUp) return;

    setIsLoading(true);

    try {
      const mutation = pendingSocialSignUp.provider === 'google' ? googleSignUp : facebookSignUp;
      const referralCode = getStoredReferralCode();
      const body =
        pendingSocialSignUp.provider === 'google'
          ? { token: pendingSocialSignUp.token, ...(referralCode && { referralCode }) }
          : { accessToken: pendingSocialSignUp.accessToken, ...(referralCode && { referralCode }) };

      const result = await mutation(body).unwrap();

      clearStoredReferralCode();
      trackEvent(AnalyticsEvents.SIGN_UP, { method: pendingSocialSignUp.provider });
      trackGoogleAdsConversion(result.data?._id);
      setPendingSocialSignUp(null);
      onClose();
      userSignIn({ token: result.access_token, redirectUrl: getRedirectUrl(result.data) });
    } catch {
      setPendingSocialSignUp(null);
    } finally {
      setIsLoading(false);
    }
  }, [pendingSocialSignUp, googleSignUp, facebookSignUp, onClose, userSignIn]);

  const handleCancelSocialSignUp = useCallback(() => {
    setPendingSocialSignUp(null);
  }, []);

  const handleTwitterSignIn = useCallback(() => {
    socialLoginRedirect('twitter');
  }, []);

  const handleForgotPassword = useCallback(
    async (data) => {
      setIsLoading(true);

      try {
        await forgotPassword(data).unwrap();
        trackEvent(AnalyticsEvents.PASSWORD_RESET_REQUESTED);
        setActiveForm('login');
        onClose();
        notificationApi[NOTIFICATION_TYPES.success]({
          message: 'A reset link has been sent to your email, please check your inbox.',
          pauseOnHover: true,
        });
      } catch {
        notificationApi[NOTIFICATION_TYPES.error]({
          message: 'An error occurred while processing your request. Please try again later.',
          pauseOnHover: true,
        });
      } finally {
        setIsLoading(false);
      }
    },
    [forgotPassword, onClose, notificationApi],
  );

  return (
    <Modal
      className="login-modal-container p-0"
      isOpen={isOpen}
      onOk={onOk}
      onCancel={onClose}
      footer={null}
      width="90%"
    >
      <StyledLoginModal>
        <div className={b()}>
          <div className={b('body')}>
            {screens.md && (
              <div className={b('body', 'left-column')}>
                <div className={b('body', 'left-column', 'title')}>
                  <Heading4>Welcome Back</Heading4>
                </div>
                <div className={b('body', 'left-column', 'animation')}>
                  <LottiePlayer animationData={login} options={lottieDefaultOptions} />
                </div>
              </div>
            )}
            <div className={b('body', 'right-column')}>
              {!screens.md && (
                <div className={b('body', 'right-column', 'animation')}>
                  <LottiePlayer animationData={login} options={lottieDefaultOptions} />
                </div>
              )}

              <Heading2>{activeForm === 'login' ? 'Sign In' : 'Forgot Password'}</Heading2>
              {!pendingSocialSignUp && (
                <>
                  {activeForm === 'login' && (
                    <LoginForm
                      loading={isLoading}
                      onSignIn={handleSignIn}
                      onGoogleSignIn={handleGoogleSignIn}
                      onFacebookSignIn={handleFacebookSignIn}
                      onTwitterSignIn={handleTwitterSignIn}
                      onSwitchTo={(form) => setActiveForm(form)}
                    />
                  )}
                  {activeForm === 'forgot' && (
                    <ForgotForm
                      loading={isLoading}
                      onForgot={handleForgotPassword}
                      onSwitchTo={(form) => setActiveForm(form)}
                    />
                  )}
                </>
              )}
              {pendingSocialSignUp && (
                <div className={b('body', 'social-reg-column')}>
                  <Heading11>The user does not exist in Active Shape, do you want to create it now?</Heading11>

                  <div className={b('body', 'social-reg-column', 'buttons')}>
                    <GradientButton variant={'secondary'} onClick={handleCancelSocialSignUp}>
                      No
                    </GradientButton>

                    <GradientButton loading={isLoading} onClick={handleConfirmSocialSignUp}>
                      Yes
                    </GradientButton>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </StyledLoginModal>
    </Modal>
  );
};

export default LoginModal;
